import React from 'react';
import styled from 'styled-components';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { checkDispensaryDPOSIntegration } from 'shared/helpers/check-dispensary-integration';
import { isEmptyRecord } from 'shared/utils/type-utils';
import { useDispensary } from 'src/hooks/use-dispensary';
import { GqlSpecials } from 'types/graphql';

import { Skeleton } from '../loading-skelelton';
import { OfferCard } from './offer-card';
import { OfferCardV2 } from './offer-card/offer-card-v2';

type OfferCarouselProps = {
  label: string;
  loading: boolean;
  offers: GqlSpecials[] | undefined;
};

export const TEST_ID_OFFER_CAROUSEL = 'offer-carousel';

export function OfferCarousel({ label, offers, loading }: OfferCarouselProps): JSX.Element | null {
  const flags = useFlags();
  const dispensary = useDispensary();
  // Match marketplace's specials redesign gate (`useSpecialsRedesignEnabled`), not just the rollout flag:
  // flag + per-dispensary `showSpecialsRedesign` toggle + DPOS/fullstack integration. Otherwise the preview
  // can show v2 while the storefront still shows v1.
  const specialsCardRedesignEnabled =
    (flags['ecomm.specials-redesign.rollout'] ?? false) &&
    !isEmptyRecord(dispensary) &&
    Boolean(dispensary.storeSettings.specialsPage?.showSpecialsRedesign) &&
    checkDispensaryDPOSIntegration(dispensary);

  if (!offers || loading) {
    return (
      <LoadingContainer>
        <Skeleton
          width={specialsCardRedesignEnabled ? 358 : 310}
          height={specialsCardRedesignEnabled ? 234 : 160}
          borderRadius={specialsCardRedesignEnabled ? 12 : 20}
        />
      </LoadingContainer>
    );
  }

  if (!offers.length) {
    return null;
  }

  return (
    <Container data-testid={TEST_ID_OFFER_CAROUSEL}>
      <Header>
        <Title>{label}</Title>
        <ViewAllLink>View all</ViewAllLink>
      </Header>

      <CardsContainer $cardWidth={specialsCardRedesignEnabled ? 358 : 310}>
        {offers.map((offer: GqlSpecials) =>
          specialsCardRedesignEnabled ? (
            <OfferCardV2 {...offer} key={offer._id} />
          ) : (
            <OfferCard {...offer} key={offer._id} />
          )
        )}
      </CardsContainer>
    </Container>
  );
}

const Container = styled.div`
  padding: 40px 16px 5px 16px;
  border-bottom: 1px solid #d7e4eb;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  align-items: center;
`;

const Title = styled.h2`
  color: ${({ theme }) => (theme.isBackgroundDark ? '#ffffff' : '#242526')};
  font-weight: bold;
  font-size: 20px;
`;

const ViewAllLink = styled.a`
  color: ${({ theme }) => theme.linkColor};
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  text-transform: ${({ theme }) => theme.textTransforms.links};
`;

const CardsContainer = styled.div<{ $cardWidth: number }>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(auto-fill, ${({ $cardWidth }) => $cardWidth}px);
  grid-gap: 11px;
  margin-top: 15px;
  padding: 10px 0 18px 0;
  margin-left: -30px;
  padding-left: 30px;
  overflow-x: scroll;
  ::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }
`;

const LoadingContainer = styled.div`
  padding: 30px 20px;
`;
